/**
 * Shared sections used by every Landing variant. Each section is
 * self-contained — it pulls its copy from the `site.*` i18n tree
 * and renders the same regardless of which variant is hosting it.
 *
 * Variant-specific framing (hero, marquee, brand scene) lives in the
 * individual Landing* files.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import {
  ActivityIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  PlusIcon,
  SendIcon,
  ShareIcon,
  ShieldIcon,
  SparkleIcon,
  WalletIcon,
} from '../components/ui';
import {
  Aurora,
  BOT_URL,
  Reveal,
  SectionHeading,
} from './shared';

/* ── Section: How it works (4 steps) ──────────────────────────── */

const STEPS = [
  { n: 1, Icon: SendIcon },
  { n: 2, Icon: PlusIcon },
  { n: 3, Icon: SparkleIcon },
  { n: 4, Icon: ShareIcon },
] as const;

export function SectionHow() {
  const { t } = useTranslation();
  return (
    <section
      id="how"
      className="scroll-mt-24 border-t border-pb-border-soft"
    >
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 lg:py-28">
        <Reveal>
          <SectionHeading
            eyebrow={t('site.how.eyebrow')}
            title={t('site.how.title')}
          />
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:gap-5">
          {STEPS.map(({ n, Icon }, i) => (
            <Reveal key={n} delay={i * 80}>
              <article className="group relative h-full overflow-hidden rounded-pb-xl border border-pb-border-soft bg-pb-paper p-6 transition-colors hover:border-pb-accent-line sm:p-7">
                <div className="flex items-start justify-between gap-4">
                  <span className="pb-numeral-lg text-[44px] leading-none text-pb-accent">
                    {String(n).padStart(2, '0')}
                  </span>
                  <span className="flex h-10 w-10 items-center justify-center rounded-pb-md bg-pb-accent-soft text-pb-accent">
                    <Icon size={20} />
                  </span>
                </div>
                <h3 className="mt-6 text-[19px] font-semibold leading-tight">
                  {t(`site.how.step${n}_title`)}
                </h3>
                <p className="mt-2 max-w-md text-[14px] leading-relaxed text-tg-hint">
                  {t(`site.how.step${n}_body`)}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Features (5 cards) ──────────────────────────────── */

const FEATURES = [
  { key: 'adapts', Icon: ActivityIcon, tone: 'accent' },
  { key: 'control', Icon: WalletIcon, tone: 'success' },
  { key: 'transparency', Icon: CheckIcon, tone: 'accent' },
  { key: 'fee', Icon: SparkleIcon, tone: 'success' },
  { key: 'access', Icon: SendIcon, tone: 'accent' },
] as const;

export function SectionFeatures() {
  const { t } = useTranslation();
  return (
    <section
      id="features"
      className="relative scroll-mt-24 overflow-hidden border-t border-pb-border-soft"
    >
      <Aurora variant="subtle" />
      <div className="relative mx-auto max-w-6xl px-5 py-20 sm:px-6 lg:py-28">
        <Reveal>
          <SectionHeading
            eyebrow={t('site.features.eyebrow')}
            title={t('site.features.title')}
          />
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          {FEATURES.map(({ key, Icon, tone }, i) => (
            <Reveal key={key} delay={(i % 3) * 90}>
              <article className="group relative h-full overflow-hidden rounded-pb-xl border border-pb-border-soft bg-pb-paper p-6 transition-all hover:border-pb-accent-line hover:shadow-pb-md">
                <span
                  className={clsx(
                    'flex h-12 w-12 items-center justify-center rounded-pb-md',
                    tone === 'accent' && 'bg-pb-accent-soft text-pb-accent',
                    tone === 'success' && 'bg-pb-success-soft text-pb-success',
                  )}
                >
                  <Icon size={22} />
                </span>
                <h3 className="mt-5 text-[17px] font-semibold">
                  {t(`site.features.${key}_title`)}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-tg-hint">
                  {t(`site.features.${key}_body`)}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Security (3 trust pillars) ──────────────────────── */

const SECURITY_POINTS = [
  { key: 'point1', Icon: WalletIcon },
  { key: 'point2', Icon: CheckIcon },
  { key: 'point3', Icon: ShieldIcon },
] as const;

export function SectionSecurity() {
  const { t } = useTranslation();
  return (
    <section
      id="security"
      className="scroll-mt-24 border-t border-pb-border-soft"
    >
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 lg:py-28">
        <Reveal>
          <SectionHeading
            eyebrow={t('site.security.eyebrow')}
            title={t('site.security.title')}
            subtitle={t('site.security.body')}
          />
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          {SECURITY_POINTS.map(({ key, Icon }, i) => (
            <Reveal key={key} delay={i * 90}>
              <article className="relative h-full overflow-hidden rounded-pb-xl border border-pb-border-soft bg-pb-paper p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-pb-accent-soft text-pb-accent">
                  <Icon size={22} />
                </span>
                <h3 className="mt-5 text-[18px] font-semibold leading-tight">
                  {t(`site.security.${key}_title`)}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-tg-hint">
                  {t(`site.security.${key}_body`)}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Fees ────────────────────────────────────────────── */

export function SectionFees() {
  const { t } = useTranslation();
  const cells = [
    { label: t('site.fees.example_gain'), value: '+$100', tone: 'success' as const },
    { label: t('site.fees.example_fee'), value: '−$20', tone: 'hint' as const },
    { label: t('site.fees.example_keep'), value: '$80', tone: 'accent' as const, strong: true },
  ];
  return (
    <section
      id="fees"
      className="scroll-mt-24 border-t border-pb-border-soft"
    >
      <div className="mx-auto max-w-3xl px-5 py-20 text-center sm:px-6 lg:py-28">
        <Reveal>
          <SectionHeading
            eyebrow={t('site.fees.eyebrow')}
            title={t('site.fees.title')}
            subtitle={t('site.fees.body')}
            center
          />
          <div className="mt-10 rounded-pb-xl border border-pb-border-soft bg-pb-paper p-5 shadow-pb-lg sm:p-7">
            {/* Stack vertically on small phones so each cell + arrow
                gets enough room; switch to horizontal at sm+. The
                arrow rotates 90° on mobile to point down. */}
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:gap-3">
              {cells.map((c, i) => (
                <div
                  key={c.label}
                  className="flex flex-col items-center gap-3 sm:flex-1 sm:flex-row"
                >
                  <div className="text-center sm:flex-1">
                    <div className="pb-eyebrow">{c.label}</div>
                    <div
                      className={clsx(
                        'pb-numeral mt-1.5 text-[26px] leading-none sm:text-[28px]',
                        c.tone === 'success' && 'text-pb-success',
                        c.tone === 'hint' && 'text-tg-hint',
                        c.tone === 'accent' && 'text-pb-accent',
                        c.strong && 'font-semibold',
                      )}
                    >
                      {c.value}
                    </div>
                  </div>
                  {i < cells.length - 1 ? (
                    <ArrowRightIcon
                      size={16}
                      className="shrink-0 rotate-90 self-center text-tg-hint sm:rotate-0"
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <p className="pb-caption mx-auto mt-5 max-w-md">
            {t('site.fees.hwm_note')}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ── Section: FAQ ─────────────────────────────────────────────── */

export function SectionFaq() {
  const { t } = useTranslation();
  const items = [1, 2, 3, 4, 5, 6] as const;
  return (
    <section
      id="faq"
      className="scroll-mt-24 border-t border-pb-border-soft"
    >
      <div className="mx-auto max-w-3xl px-5 py-20 sm:px-6 lg:py-28">
        <Reveal>
          <SectionHeading
            eyebrow={t('site.faq.eyebrow')}
            title={t('site.faq.title')}
          />
        </Reveal>
        <Reveal delay={80}>
          <div className="mt-10 divide-y divide-pb-border-soft overflow-hidden rounded-pb-xl border border-pb-border-soft bg-pb-paper">
            {items.map((n) => (
              <FaqItem
                key={n}
                question={t(`site.faq.q${n}`)}
                answer={t(`site.faq.a${n}`)}
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FaqItem({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="group">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="pb-focus-ring flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-white/[0.02] sm:px-6"
      >
        <span className="text-[15px] font-medium sm:text-[16px]">
          {question}
        </span>
        <span
          aria-hidden
          className={clsx(
            'shrink-0 text-tg-hint transition-transform duration-300',
            open && 'rotate-180 text-pb-accent',
          )}
        >
          <ChevronDownIcon size={18} />
        </span>
      </button>
      {open ? (
        <p className="px-5 pb-5 text-[14px] leading-relaxed text-tg-hint sm:px-6">
          {answer}
        </p>
      ) : null}
    </div>
  );
}

/* ── Section: Closing CTA ─────────────────────────────────────── */

export function SectionCta() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden border-t border-pb-border-soft">
      <Aurora variant="wide" />
      <div className="relative mx-auto max-w-3xl px-5 py-24 text-center sm:px-6">
        <Reveal>
          <h2 className="pb-display text-[clamp(28px,4.6vw,52px)] leading-[1.05]">
            {t('site.cta.title')}
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-tg-hint">
            {t('site.cta.body')}
          </p>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pb-btn pb-btn-primary pb-focus-ring mt-9 inline-flex px-8 text-[15px]"
          >
            {t('site.cta.button')}
          </a>
        </Reveal>
      </div>
    </section>
  );
}
